import { useMemo, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMutation } from '@tanstack/react-query';
import { compareDesc, parseISO } from 'date-fns';

import type { ProjectImageType, ProjectLayoutType } from '@/api/media';
import { env } from '@/constants/env';
import { MediaImage } from '@/features/estate-media/components/media-image';
import { SelectedImage } from '@/features/estate-media/components/selected-image';
import {
  ViewMediaModal,
  type ViewMediaModalProps,
} from '@/features/estate-media/components/view-media-modal';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatDate';

import { MediaDataLayout, type MediaDataLayoutProps } from './media-data-layout';
import { useModal } from '@/components/modals/new/use-modal';

type MediaDataProps = {
  type: 'project';
  data: Record<string, ProjectImageType[] | ProjectLayoutType[]>;
  handleDelete: (ids: string[], callback: () => void) => void;
  // required so every media page decides explicitly whether write actions are shown
  canWriteMedia: boolean;
} & MediaDataLayoutProps;

export const MediaData = ({ data, handleDelete, ...props }: MediaDataProps) => {
  const { t } = useTranslation();
  const contentData = useMemo<[string, ProjectImageType[]][]>(() => {
    return Object.entries(data).sort(([date1], [date2]) =>
      compareDesc(parseISO(date1), parseISO(date2))
    );
  }, [data]);
  const mediaModal = useModal();
  const [mediaModalContext, setMediaModalContext] = useState<Omit<ViewMediaModalProps, 'modal'>>();

  const [isSelected, setSelected] = useState(false);
  const [selectedValues, setSelectedValues] = useState<
    { id: string; mediaId: string; name: string; date: Date }[]
  >([]);

  const handleSetSelectValue = () => {
    setSelected((prev) => !prev);
    setSelectedValues([]);
  };

  const handleSelect = ({
    id,
    mediaId,
    name,
    date,
  }: {
    id: string;
    mediaId: string;
    name: string;
    date: Date;
  }) => {
    //If the given image is already selected, it removes it from the selectedValues array.
    //If the given image is not already selected, it adds it to the selectedValues array.
    if (selectedValues.some((item) => item.mediaId === mediaId)) {
      setSelectedValues(selectedValues.filter((val) => val.mediaId !== mediaId));
    } else {
      setSelectedValues([...selectedValues, { id, mediaId, name, date }]);
    }
  };

  const toggleDateSelection = (images: ProjectImageType[]) => {
    setSelectedValues((prevSelected) => {
      const deletableImages = images.filter((image) => !image.isUsedByFlatfinder);
      if (!deletableImages.length) return prevSelected;

      // Check if all images for the given date are already selected
      const allSelected = deletableImages.every((img) =>
        prevSelected.some((selected) => selected.mediaId === img.mediaId)
      );

      if (allSelected) {
        // If all images are selected, remove them from the selected list (unselect all)
        return prevSelected.filter(
          (img) => !deletableImages.some((image) => image.mediaId === img.mediaId)
        );
      }

      // If not all images are selected, add the missing ones to the selection
      const newImages = deletableImages
        .map((image) => ({
          id: image.id,
          mediaId: image.mediaId || image.id,
          name: image.fileName || `image_${image.id}`,
          date: new Date(image.createdAt),
        }))
        .filter((img) => !prevSelected.some((selected) => selected.mediaId === img.mediaId)); // Avoid adding duplicates

      // Return updated selected values, merging previous selections with newly added ones
      return [...prevSelected, ...newImages];
    });
  };

  const handleDeleteSelectValues = () => {
    if (selectedValues.length === 0) {
      toast.error(t('no-files-selected'));
    } else {
      handleDelete(
        selectedValues.map((val) => val.id),
        () => {
          setSelected(false);
          setSelectedValues([]);
        }
      );
    }
  };

  const { mutate: downloadImages } = useMutation({
    mutationFn: async ({
      id,
      images,
      grouped = false,
    }: {
      id: string;
      images: { mediaId: string; date: Date; name: string }[];
      grouped?: boolean;
    }) => {
      // Use fetch directly for binary data to bypass any potential
      // processing that Eden or TanStack Query might do
      const response = await fetch(`${env.API_URL}/projects/${id}/images/zip?grouped=${grouped}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ images }),
      });

      if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
      }

      return response.arrayBuffer();
    },
    onSuccess: (data) => {
      // Handle download as before
      const blob = new Blob([data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'images.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const handleDownload = async () => {
    if (selectedValues.length === 0) {
      toast.error(t('no-files-selected'));
    } else {
      // Extract unique dates
      const uniqueDates = new Set(
        selectedValues.map((image) => image.date.toISOString().split('T')[0])
      );
      // Check if there are multiple unique dates
      const hasMultipleDates = uniqueDates.size > 1;

      downloadImages({ id: props.projectId, images: selectedValues, grouped: hasMultipleDates });
    }
  };

  const openViewMediaModal = (args: Omit<ViewMediaModalProps, 'modal'>) => () => {
    setMediaModalContext(args);
    mediaModal.open();
  };

  return (
    <>
      <MediaDataLayout
        onSelect={handleSetSelectValue}
        onDelete={handleDeleteSelectValues}
        onDownload={handleDownload}
        {...props}
      >
        <div className="py-8">
          {contentData.length > 0 ? (
            contentData.map(([date, mediaData], index) => {
              const deletableImages = mediaData.filter((image) => !image.isUsedByFlatfinder);
              const allSelected =
                deletableImages.length > 0 &&
                deletableImages.every((img) =>
                  selectedValues.some((selected) => selected.mediaId === img.mediaId)
                );
              return (
                <div key={`media-data-${String.fromCharCode(65 + index)}`} className="mb-5">
                  <button
                    type="button"
                    onClick={() => isSelected && toggleDateSelection(mediaData)}
                    className={cn(
                      isSelected && 'cursor-pointer',
                      'relative inline-flex items-center gap-2'
                    )}
                  >
                    <h3 className="py-2 text-sm">{formatDate(date, 'de-DE')}</h3>
                    {isSelected ? (
                      <div
                        className={cn(
                          'size-6 rounded-full border-2',
                          allSelected ? 'border-white bg-primary-light' : 'border-gray-300 bg-white'
                        )}
                      />
                    ) : null}
                  </button>
                  {isSelected ? (
                    <form className="grid w-full grid-cols-6 gap-1 max-md:grid-cols-3 max-sm:grid-cols-2">
                      {mediaData.map((file) => (
                        <Fragment key={file.id}>
                          <SelectedImage
                            fileName={file.fileName as string}
                            mediaId={file?.mediaId as string}
                            disabled={file.isUsedByFlatfinder}
                            disabledLabel={t('imageInUse')}
                            onClick={() => {
                              handleSelect({
                                id: file.id,
                                mediaId: file.mediaId || file.id,
                                name: file.fileName || `image_${file.id}`,
                                date: new Date(file.createdAt),
                              });
                            }}
                            isSelected={selectedValues.some(
                              (item) => item.mediaId === file.mediaId
                            )}
                          />
                        </Fragment>
                      ))}
                    </form>
                  ) : (
                    <div className="grid w-full grid-cols-6 gap-1 max-md:grid-cols-3 max-sm:grid-cols-2">
                      {mediaData.map((file, i) => (
                        <button
                          type="button"
                          key={file.id}
                          className="cursor-pointer whitespace-normal break-words px-1"
                          onClick={openViewMediaModal({
                            images: mediaData,
                            preselectedCurrentIndex: i,
                          })}
                        >
                          <MediaImage
                            mediaId={file.mediaId as string}
                            fileName={file.fileName as string}
                            isRounded
                          />
                          <p className="w-full max-w-52 truncate text-sm text-text-secondary">
                            {file.fileName}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex justify-center p-5">
              <p>{t('noContent')}</p>
            </div>
          )}
        </div>
      </MediaDataLayout>
      {mediaModalContext ? (
        <ViewMediaModal
          projectId={props.projectId}
          {...mediaModalContext}
          imageBeautificationButtons={props.canWriteMedia}
          modal={mediaModal}
        />
      ) : null}
    </>
  );
};
